/**
 * Date utility functions for consistent date formatting
 *
 * IMPORTANT: Always use these utilities instead of toISOString() for date-only values
 * to avoid timezone conversion bugs.
 *
 * The issue: toISOString() converts to UTC, which can shift dates by a day depending
 * on the user's timezone. For example, if it's Oct 1st at 11 PM PST (UTC-8),
 * toISOString() would return Oct 2nd in UTC.
 */

/**
 * Formats a Date object to YYYY-MM-DD string in LOCAL timezone
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format (local timezone)
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Gets a date range for the last N days (inclusive)
 * @param days - Number of days to include in the range
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function getRollingDateRange(days: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - Math.max(0, days - 1));
  return {
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(end),
  };
}

/**
 * Gets the last full calendar month
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 *
 * Examples:
 * - If today is Dec 15, 2024, returns Nov 1-30, 2024
 * - If today is Jan 5, 2025, returns Dec 1-31, 2024
 * - If today is Mar 15, 2024 (leap year), returns Feb 1-29, 2024
 */
export function getLastFullMonth(): { startDate: string; endDate: string } {
  const now = new Date();

  // Get first day of current month
  const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // Go back one day to get into the previous month
  const lastDayOfPrevMonth = new Date(firstOfCurrentMonth);
  lastDayOfPrevMonth.setDate(0); // setDate(0) goes to last day of previous month

  // Get first day of previous month
  const firstDayOfPrevMonth = new Date(
    lastDayOfPrevMonth.getFullYear(),
    lastDayOfPrevMonth.getMonth(),
    1,
  );

  return {
    startDate: formatLocalDate(firstDayOfPrevMonth),
    endDate: formatLocalDate(lastDayOfPrevMonth),
  };
}

/**
 * Gets the default date range (entire month of July for the current year)
 *
 * This ensures users always see data when they log in by loading a
 * consistent, populated timeframe across all tables.
 *
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function getDefaultDateRange(): { startDate: string; endDate: string } {
  const dataYear = 2025; // Year with available data - update when more recent data becomes default
  const startOfJuly = new Date(dataYear, 6, 1); // July is month 6 (0-indexed)
  const endOfJuly = new Date(dataYear, 7, 0); // Last day of July

  return {
    startDate: formatLocalDate(startOfJuly),
    endDate: formatLocalDate(endOfJuly),
  };
}

/**
 * Formats a YYYY-MM string to human-readable month and year in UTC
 * @param monthStr - Month string in YYYY-MM format (e.g., "2026-01")
 * @returns Formatted string like "January 2026"
 *
 * IMPORTANT: Uses UTC to prevent timezone shift where "2026-01" could display
 * as "December 2025" in US timezones when parsed as local time.
 */
export function formatYearMonthUTC(monthStr: string): string {
  const [yearStr, monthNumStr] = monthStr.split('-');
  const year = Number(yearStr);
  const monthNum = Number(monthNumStr); // 1-12
  const date = new Date(Date.UTC(year, monthNum - 1, 1));
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
  }).format(date);
}

/**
 * Formats a YYYY-MM string to abbreviated month and year in UTC
 * @param monthStr - Month string in YYYY-MM format (e.g., "2026-01")
 * @returns Formatted string like "Jan 2026"
 *
 * IMPORTANT: Uses UTC to prevent timezone shift where "2026-01" could display
 * as "December 2025" in US timezones when parsed as local time.
 */
export function formatYearMonthShortUTC(monthStr: string): string {
  const [yearStr, monthNumStr] = monthStr.split('-');
  const year = Number(yearStr);
  const monthNum = Number(monthNumStr); // 1-12
  const date = new Date(Date.UTC(year, monthNum - 1, 1));
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
  }).format(date);
}
